import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Loader2, Instagram } from "lucide-react";
import CaptchaField, { execRecaptchaV3 } from "@/components/CaptchaField";
import { trackEvent } from "@/lib/analytics";

const CATEGORIES = ["Fashion", "Sneakers", "Beauty", "Home", "Tech", "Accessories", "Food & Drink", "Art & Design", "Other"];
const empty = { name: "", email: "", phone: "", instagram: "", website: "", store_name: "", category: "", country: "", message: "" };
const REQUIRED = ["name", "email", "phone", "instagram", "website", "store_name", "category", "country", "message"];

export default function SignupForm({ onSuccess }) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState({ provider: "none", site_key: "" });
  const [token, setToken] = useState("");
  const [igEnabled, setIgEnabled] = useState(false);

  useEffect(() => {
    api.get("/public/captcha").then((r) => setCaptcha(r.data || { provider: "none" })).catch(() => {});
    api.get("/public/instagram/config").then((r) => setIgEnabled(!!r.data?.enabled)).catch(() => {});
  }, []);

  const onToken = useCallback((t) => setToken(t), []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const connectInstagram = () => {
    trackEvent("merchant_signup", "sign_up", { method: "instagram" });
    window.location.href = `${API}/auth/instagram/start?origin=${encodeURIComponent(window.location.origin)}`;
  };

  const submit = async () => {
    for (const k of REQUIRED) {
      if (!String(form[k] || "").trim()) { toast.error("Please fill in all fields."); return; }
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Please enter a valid email address."); return; }

    let captchaToken = token;
    const p = captcha.provider;
    if (p === "recaptcha_v3") {
      try { captchaToken = await execRecaptchaV3(captcha.site_key, "signup"); }
      catch { toast.error("Captcha failed. Please try again."); return; }
    } else if (p && p !== "none") {
      if (!captchaToken) { toast.error("Please complete the captcha."); return; }
    }

    setSubmitting(true);
    try {
      await api.post("/signup", { ...form, captcha_token: captchaToken });
      trackEvent("merchant_signup", "sign_up", { category: form.category, country: form.country, method: "form" });
      toast.success("You're on the list! We'll be in touch soon.");
      setForm(empty); setToken("");
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3.5 mt-2">
      {igEnabled && (
        <div className="mb-1">
          <button type="button" onClick={connectInstagram} data-testid="signup-instagram-connect"
            className="w-full rounded-full py-3.5 font-semibold text-white flex items-center justify-center gap-2 active:scale-95 transition-transform"
            style={{ background: "linear-gradient(90deg,#833ab4,#fd1d1d,#fcb045)" }}>
            <Instagram size={17} /> Continue with Instagram
          </button>
          <div className="flex items-center gap-3 my-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-semibold">or fill in manually</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Your name"><input data-testid="signup-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="sfin" /></Field>
        <Field label="Email"><input data-testid="signup-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="sfin" /></Field>
        <Field label="Phone"><input data-testid="signup-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="sfin" /></Field>
        <Field label="Instagram handle"><input data-testid="signup-instagram" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@yourbrand" className="sfin" /></Field>
        <Field label="Website"><input data-testid="signup-website" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="sfin" /></Field>
        <Field label="Store name"><input data-testid="signup-store" value={form.store_name} onChange={(e) => set("store_name", e.target.value)} className="sfin" /></Field>
        <Field label="Category">
          <select data-testid="signup-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="sfin">
            <option value="">Select…</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Country"><input data-testid="signup-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="sfin" /></Field>
      </div>
      <Field label="Message"><textarea data-testid="signup-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="sfin" /></Field>

      {captcha.provider && captcha.provider !== "none" && captcha.provider !== "recaptcha_v3" && (
        <div className="pt-1"><CaptchaField provider={captcha.provider} siteKey={captcha.site_key} onToken={onToken} /></div>
      )}

      <button onClick={submit} disabled={submitting} data-testid="signup-submit"
        className="w-full bg-black text-white rounded-full py-3.5 font-semibold disabled:opacity-60 flex items-center justify-center gap-2 active:scale-95 transition-transform">
        {submitting && <Loader2 size={16} className="animate-spin" />} Request access
      </button>
      <p className="text-[11px] text-slate-400 text-center">All fields are required.</p>

      <style>{`.sfin{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:9px 12px;font-size:14px;outline:none;font-family:inherit;background:#fff}.sfin:focus{border-color:#000}`}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-heading font-bold uppercase tracking-[0.12em] text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);
